import React, { useState } from 'react';
import './CreateQuestion.styles.css';
import { Input, InputVariants, Spinner, SpinnerVariants } from '../../UI';
import { useInput } from '../../hooks/useInput';
import { Link, useNavigate } from 'react-router-dom';
import { useAddQuestionMutation, useLazyGetQuestionsByTitleQuery } from '../../apis';
import type { Question } from '../../types';

// Normalize for the similarity lookup: trim, collapse whitespace, drop trailing
// punctuation so "Did X win?" matches "did x win" (backend search is case-insensitive).
const normalize = (title: string) =>
  title.trim().replace(/\s+/g, ' ').replace(/[?!.]+$/, '');

export const CreateQuestion = () => {

  const [addQuestion, { isLoading }] = useAddQuestionMutation();
  const [findSimilar, { isFetching: isChecking }] = useLazyGetQuestionsByTitleQuery();

  const navigate = useNavigate();

  const {
    inputValue: newQuestionValue,
    hasError: newQuestionError,
    onInputChange,
    onInputBlur: onNewQuestionBlur,
  } = useInput((value) => value.trim().length > 0);

  const [serverError, setServerError] = useState<string | null>(null);
  // null = not yet checked, or checked with no matches (we post immediately);
  // a non-empty array = similar questions found, awaiting the user's decision.
  const [similar, setSimilar] = useState<Question[] | null>(null);

  // Editing the title invalidates a previous similarity check.
  const onQuestionChange = (value: string) => {
    if (similar !== null) setSimilar(null);
    onInputChange(value);
  };

  const submitAddQuestion = async (title: string) => {
    const result = await addQuestion({ title });
    if ('error' in result && result.error) {
      const errorData = result.error as { data?: { message?: string } };
      setServerError(errorData?.data?.message || 'Failed to add question');
    } else {
      const userId = localStorage.getItem('userId');
      navigate(`/user-questions/${userId}`);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const title = newQuestionValue.trim();
    if (title.length <= 0) return;
    setServerError(null);

    // Suggestions already shown -> the user chose "Post anyway".
    if (similar !== null) {
      return submitAddQuestion(title);
    }

    // First submit -> nudge with similar existing questions (never blocks).
    try {
      const matches = await findSimilar({ title: normalize(title) }).unwrap();
      if (matches && matches.length > 0) {
        setSimilar(matches);
        return; // let the user vote on one or post anyway
      }
    } catch {
      // by-title lookup failed -> treat as "no similar" and just post
    }
    submitAddQuestion(title);
  };

  const busy = isLoading || isChecking;

  return (
    <>
      {busy && <Spinner variant={SpinnerVariants.CIRCLE} />}
      <div className="signin-form-wrapper">
        <div className="signin-form-container">
          {serverError
            ? <span className="text-app-base text-red-500 mb-app-xl">{serverError}</span>
            : null}

          <form onSubmit={handleSubmit} className="flex flex-1 flex-col justify-center gap-app-xl ">
            <Input
              variant={InputVariants.OUTLINE}
              name={'add-question'}
              id={'add-question-id'}
              value={newQuestionValue}
              errorMessage={'Question is required'}
              hasError={newQuestionError}
              onChange={onQuestionChange}
              onBlur={onNewQuestionBlur}
              required={true}
            />

            {similar && similar.length > 0 && (
              <div className="flex flex-col gap-app-base rounded-app-base border border-app-third p-app-base">
                <span className="text-app-base text-app-gray">
                  Similar questions already exist — vote on one, or post yours anyway:
                </span>
                <ul className="flex flex-col gap-app-s">
                  {similar.slice(0, 5).map((q) => (
                    <li key={q.questionId}>
                      <Link
                        to={`/question/${q.questionId}`}
                        className="flex justify-between gap-app-base text-app-primary hover:underline"
                      >
                        <span className="truncate">{q.title}</span>
                        <span className="shrink-0 text-app-gray">
                          {(q.likes ?? 0) + (q.dislikes ?? 0)} votes
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <button
              disabled={busy}
              className="flex mt-app-base p-app-base rounded-app-base justify-center bg-[#3742fa] hover:bg-[#2828dc] disabled:opacity-40 disabled:hover:bg-[#3742fa]">
              {similar ? 'Post anyway' : 'Add question'}
            </button>
          </form>
        </div>
      </div>
    </>
  );
};
